import { ChangeDetectionStrategy, Component, inject, InjectionToken } from '@angular/core';

/**
 * Re-runs the load-and-bootstrap sequence in place (no location.reload(), so
 * the retry is unit-testable).
 */
export const CONFIG_RETRY = new InjectionToken<() => void>('CONFIG_RETRY');

/**
 * Full-page configuration failure (FR-034, SC-010). Deliberately
 * dependency-free — tokens only, no SessionService, no router, no icons — so
 * a config failure cannot cascade.
 */
@Component({
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <main class="config-error">
      <h1>We can’t start the application</h1>
      <p>
        The application’s runtime settings could not be loaded. This is usually
        a temporary problem — check your connection and try again. If it keeps
        happening, contact support.
      </p>
      <button type="button" (click)="retry()">Retry</button>
    </main>
  `,
  styles: `
    .config-error {
      max-width: 32rem;
      margin: 15vh auto 0;
      padding: var(--space-xl);
      background: var(--color-surface);
      border: 1px solid var(--color-border-light);
      border-left: 3px solid var(--color-error);
      border-radius: var(--radius-base);
      color: var(--color-text);
    }
    h1 {
      margin: 0 0 var(--space-md);
      font-size: var(--font-size-xl);
    }
    p {
      margin: 0 0 var(--space-lg);
      color: var(--color-text-muted);
    }
    button {
      padding: var(--space-2xs) var(--space-md);
      background: var(--color-primary);
      color: var(--color-surface);
      border: 1px solid var(--color-primary);
      border-radius: var(--radius-sm);
      cursor: pointer;
    }
  `,
})
export class ConfigErrorComponent {
  private readonly configRetry = inject(CONFIG_RETRY);

  retry(): void {
    this.configRetry();
  }
}
